import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Save } from 'lucide-react'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { BackupService } from '@/lib/services/backupService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useThemeColors } from '@/hooks/useThemeColors'
import type { CreateBackupData } from '@/types/backup'

export default function CreateBackupPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const { showError, showSuccess } = useToastNotification()

  const [isLoading, setIsLoading] = useState(false)
  const [databases, setDatabases] = useState<{ value: string; label: string }[]>([])
  const [tables, setTables] = useState<string[]>([])
  const [formData, setFormData] = useState<CreateBackupData>({
    name: '',
    type: 'full',
    compression: 'gzip',
    encrypted: false,
    storage_disk: 'local',
    retention_days: 30,
  })

  const isDbOrFull = formData.type === 'database' || formData.type === 'full'

  useEffect(() => {
    const fetchDatabases = async () => {
      try {
        const list = await BackupService.getBackupDatabases()
        setDatabases(list ?? [])
      } catch (error: any) {
        showError(error)
      }
    }
    fetchDatabases()
  }, [showError])

  useEffect(() => {
    if (!isDbOrFull) {
      setTables([])
      return
    }
    const fetchTables = async () => {
      try {
        const tablesData = await BackupService.getBackupTables(formData.database || undefined)
        setTables(tablesData ?? [])
      } catch (error: any) {
        showError(error)
      }
    }
    fetchTables()
  }, [isDbOrFull, formData.database, showError])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await BackupService.createBackup(formData)
      showSuccess('Backup created successfully')
      navigate('/system-settings/backup-restore')
    } catch (error: any) {
      showError(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleChange = (field: keyof CreateBackupData, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'database') next.tables_included = undefined
      if (field === 'type' && value === 'files') {
        next.database = undefined
        next.tables_included = undefined
      }
      return next
    })
  }

  return (
    <AdminLayout>
      <Box p={4} h="full" overflow="hidden" display="flex" flexDirection="column">
        <Card h="full" display="flex" flexDirection="column">
          <CardHeader flexShrink={0}>
            <Heading size="md">Create Backup</Heading>
            <Text fontSize="sm" color={colors.text} mt={1}>Create a new system backup</Text>
          </CardHeader>
          <CardBody overflowY="auto" flex={1} minH={0}>
            <form onSubmit={handleSubmit}>
              <VStack spacing={6} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Backup Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Enter backup name"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Backup Type</FormLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value as CreateBackupData['type'])}
                  >
                    <option value="database">Database Only</option>
                    <option value="files">Files Only</option>
                    <option value="full">Full Backup (Database + Files)</option>
                  </Select>
                </FormControl>

                {isDbOrFull && (
                  <FormControl>
                    <FormLabel>Database</FormLabel>
                    <Select
                      value={formData.database ?? ''}
                      onChange={(e) => handleChange('database', e.target.value || undefined)}
                    >
                      <option value="">Default (connection default)</option>
                      {databases.map((db) => (
                        <option key={db.value} value={db.value}>
                          {db.label}
                        </option>
                      ))}
                    </Select>
                    <Text mt={1} fontSize="xs" color={colors.textMuted}>
                      Choose which database to back up. Leave as Default to use the app&apos;s default connection.
                    </Text>
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel>Compression</FormLabel>
                  <Select
                    value={formData.compression ?? 'gzip'}
                    onChange={(e) => handleChange('compression', e.target.value as CreateBackupData['compression'])}
                  >
                    <option value="none">None</option>
                    <option value="gzip">Gzip</option>
                    <option value="zip">Zip</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Storage Disk</FormLabel>
                  <Select
                    value={formData.storage_disk ?? 'local'}
                    onChange={(e) => handleChange('storage_disk', e.target.value as CreateBackupData['storage_disk'])}
                  >
                    <option value="local">Local Storage</option>
                    <option value="s3">S3 Storage</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <Checkbox
                    isChecked={formData.encrypted ?? false}
                    onChange={(e) => handleChange('encrypted', e.target.checked)}
                  >
                    Encrypt Backup
                  </Checkbox>
                </FormControl>

                {(formData.type === 'database' || formData.type === 'full') && (
                  <FormControl>
                    <HStack justify="space-between" mb={2}>
                      <FormLabel mb={0}>Select Tables (leave empty for all)</FormLabel>
                      {tables.length > 0 && (
                        <HStack spacing={2}>
                          <Text fontSize="sm" color={colors.textMuted}>
                            {formData.tables_included?.length || 0} of {tables.length} selected
                          </Text>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => {
                              const allSelected = (formData.tables_included?.length ?? 0) === tables.length
                              handleChange('tables_included', allSelected ? undefined : tables)
                            }}
                          >
                            {(formData.tables_included?.length ?? 0) === tables.length ? 'Uncheck All' : 'Check All'}
                          </Button>
                        </HStack>
                      )}
                    </HStack>
                    {tables.length > 0 ? (
                      <Box border="1px" borderColor={colors.border} borderRadius="md" p={4} maxH="300px" overflowY="auto" bg={colors.sectionBg}>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                          {tables.map((table) => (
                            <Checkbox
                              key={table}
                              isChecked={formData.tables_included?.includes(table) ?? false}
                              onChange={(e) => {
                                const current = formData.tables_included ?? []
                                const updated = e.target.checked ? [...current, table] : current.filter((t) => t !== table)
                                handleChange('tables_included', updated.length > 0 ? updated : undefined)
                              }}
                            >
                              {table}
                            </Checkbox>
                          ))}
                        </SimpleGrid>
                      </Box>
                    ) : (
                      <Box p={4} border="1px" borderColor={colors.border} borderRadius="md" bg={colors.sectionBg}>
                        <Text fontSize="sm" color={colors.textMuted}>Loading tables...</Text>
                      </Box>
                    )}
                    <Text mt={2} fontSize="xs" color={colors.textMuted}>
                      {formData.tables_included && formData.tables_included.length > 0
                        ? `Selected: ${formData.tables_included.join(', ')}`
                        : 'No tables selected - all tables will be backed up'}
                    </Text>
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel>Retention Days (optional)</FormLabel>
                  <NumberInput
                    value={formData.retention_days ?? ''}
                    onChange={(_, value) => handleChange('retention_days', value && value >= 1 ? value : undefined)}
                    min={1}
                  >
                    <NumberInputField placeholder="30" />
                  </NumberInput>
                </FormControl>

                <HStack justify="flex-end">
                  <Button variant="ghost" onClick={() => navigate('/system-settings/backup-restore')}>Cancel</Button>
                  <Button type="submit" colorScheme="blue" leftIcon={<Save size={18} />} isLoading={isLoading} loadingText="Creating...">
                    Create Backup
                  </Button>
                </HStack>
              </VStack>
            </form>
          </CardBody>
        </Card>
      </Box>
    </AdminLayout>
  )
}
